import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import authSvc from '@efex-services/auth/authSvc';
import { IWhiteLabel } from '@efex-services/auth/types';
import { sanitizeOrganizationId } from '../utils';
import { setWhiteLabel } from '../../login/store/whiteLabel.slice';
import useWhiteLabelService from '../../login/hooks/useWhiteLabel.service';

interface UseWhiteLabelValidationProps {
  /** Debounce time in milliseconds */
  debounceMs?: number;
  /** Minimum length of the organization ID */
  minLength?: number;
}

interface UseWhiteLabelValidationReturn {
  /** Whether the organization ID is currently being validated */
  isValidating: boolean;
  /** Result of the organization ID validation */
  validationResult: IWhiteLabel | null;
  /** Error message from the organization ID validation */
  validationError: string | null;
  /** Whether the organization ID is valid */
  isValid: boolean;
  /** Function to validate the organization ID */
  validateOrganization: (organizationId: string) => void;
  /** Function to clear the validation result */
  clearValidation: () => void;
}

const DEFAULT_DEBOUNCE_MS = 500;
const DEFAULT_MIN_LENGTH = 2;

const useWhiteLabelValidation = ({
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minLength = DEFAULT_MIN_LENGTH,
}: UseWhiteLabelValidationProps = {}): UseWhiteLabelValidationReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setWhiteLabelToLocalStorage } = useWhiteLabelService();
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<IWhiteLabel | null>(
    null
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const clearValidation = useCallback(() => {
    setValidationResult(null);
    setValidationError(null);
    setIsValid(false);
    setIsValidating(false);
  }, []);

  const performValidation = useCallback(
    async (organizationId: string) => {
      try {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        abortControllerRef.current = new AbortController();

        setIsValidating(true);
        setValidationError(null);

        const sanitizedOrganizationId = sanitizeOrganizationId(organizationId);

        const result = await authSvc.getWhiteLabel({
          organization: sanitizedOrganizationId,
        });

        if (abortControllerRef.current.signal.aborted) {
          return;
        }

        setValidationResult(result);
        setIsValid(true);
        setValidationError(null);
        setWhiteLabelToLocalStorage(result);
        dispatch(setWhiteLabel(result));
      } catch (error: unknown) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        setValidationResult(null);
        setIsValid(false);

        if (error && typeof error === 'object' && 'response' in error) {
          const httpError = error as { response?: { status?: number } };
          if (
            httpError.response?.status === 404 ||
            httpError.response?.status === 400
          ) {
            setValidationError(t('regOrganizationNotFound'));
          } else if (
            httpError.response?.status &&
            httpError.response.status >= 500
          ) {
            setValidationError(t('regServerError'));
          } else {
            setValidationError(t('regInvalidOrganizationId'));
          }
        } else {
          setValidationError(t('regInvalidOrganizationId'));
        }
      } finally {
        setIsValidating(false);
      }
    },
    [t, setWhiteLabelToLocalStorage, dispatch]
  );

  const validateOrganization = useCallback(
    (organizationId: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      if (!organizationId || organizationId.length < minLength) {
        clearValidation();
        return;
      }

      debounceRef.current = setTimeout(() => {
        performValidation(organizationId);
      }, debounceMs);
    },
    [debounceMs, minLength, performValidation, clearValidation]
  );

  useEffect(
    () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    []
  );

  return {
    isValidating,
    validationResult,
    validationError,
    isValid,
    validateOrganization,
    clearValidation,
  };
};

export default useWhiteLabelValidation;
